import { useRef } from "react";
import { StickyContainer } from "../SharedUI";
import Figure2CD from "./figure2cd/Figure2CD";
import AnimatedSankey from "./figure4/Figure4";
import styled from "@emotion/styled";
import { useContainerDimensions } from "../../hooks/useContainerDimensions";
import { useSpring, animated } from "@react-spring/web";
import classification1Svg from "../../assets/classification1.svg";
import classification2Svg from "../../assets/classification2.svg";
import classification3Svg from "../../assets/classification3.svg";

const SmallImageContainer = styled.div`
  max-width: 37%;
  margin: auto;
  padding-bottom: 2em;

  @media (max-width: 600px) {
    max-width: 45%;
    padding-bottom: 4em;
  }
`;

const FullImageContainer = styled.div`
  max-width: 80%;
  margin: auto;

  @media (max-width: 600px) {
    padding-bottom: 4em;
  }
`;

interface VisualizationProps {
  currentStep: string;
}

const ClassificationStickyContainer = styled(StickyContainer)``;

export default function ClassificationViz({ currentStep }: VisualizationProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const dimensions = useContainerDimensions(containerRef);
  const renderWidth = dimensions.width;
  const renderHeight =
    renderWidth < 500 ? renderWidth * 1.5 : dimensions.height;

  const treeSteps = [
    "mapping-intro",
    "mapping-static",
    "mapping-issues",
    "compounding-losses",
  ];
  const sankeySteps = [
    "gl-method-2",
    "example-A",
    "example-B",
    "example-weights",
    "example-results",
    "classification-breakout",
  ];

  const classificationIntroSpring = useSpring({
    from: { opacity: 0 },
    to: { opacity: currentStep === "classification-intro" ? 1 : 0 },
    config: { duration: 300 },
  });

  const exampleSpring = useSpring({
    from: { opacity: 0 },
    to: { opacity: currentStep === "example" ? 1 : 0 },
    config: { duration: 300 },
  });

  return (
    <ClassificationStickyContainer ref={containerRef}>
      {(currentStep === "" ||
        currentStep === "problem-2-header" ||
        currentStep === "transition") && (
        <SmallImageContainer>
          <img
            src={classification1Svg}
            alt="classification"
            style={{ maxWidth: "100%", maxHeight: "100%", margin: "auto" }}
          />
        </SmallImageContainer>
      )}
      {currentStep === "classification-intro" && (
        <FullImageContainer>
          <animated.img
            src={classification2Svg}
            alt="classification-intro"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              margin: "auto",
              opacity: classificationIntroSpring.opacity,
            }}
          />
        </FullImageContainer>
      )}
      {currentStep === "example" && (
        <SmallImageContainer>
          <animated.img
            src={classification3Svg}
            alt="classification-example"
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              opacity: exampleSpring.opacity,
              margin: "auto",
            }}
          />
        </SmallImageContainer>
      )}
      {treeSteps.includes(currentStep) && (
        <Figure2CD
          svgWidth={renderWidth}
          svgHeight={renderHeight}
          currentStep={currentStep}
          isNarrow={renderWidth <= 570}
        />
      )}
      {sankeySteps.includes(currentStep) && (
        <AnimatedSankey
          svgWidth={renderWidth}
          svgHeight={renderHeight}
          currentStep={currentStep}
          isNarrow={renderWidth <= 700}
        />
      )}
    </ClassificationStickyContainer>
  );
}
